
import React from "react";
import type { RouteObject } from "react-router-dom";
import { Navigate, useParams } from "react-router-dom";

/** Redirects legacy /expenses/:expenseId to the canonical /operations/expenses/:expenseId */
const LegacyExpenseRedirect: React.FC = () => {
  const { expenseId } = useParams<{ expenseId: string }>();
  return <Navigate to={`/operations/expenses/${expenseId}`} replace />;
};
import { ErrorBoundary } from "@/shared/ui/ErrorBoundary";
import { ErrorState } from "@/shared/ui/states/ErrorState";
import { LoadingState } from "@/shared/ui/states/LoadingState";
import { ProtectedRoute } from "@/shared/components/ProtectedRoute";
import { AuthWrapper } from "@/app/AuthWrapper";
import { PERMISSIONS } from "@/shared/constants/permissions";
import type { AppLayoutProps } from "./types";

const DashboardNoAccessPage = React.lazy(() => import("@/pages/DashboardNoAccessPage"));

// Lazy load operations features
const Trips = React.lazy(() => import("@/features/trips/components/Trips"));
const NewTrip = React.lazy(() => import("@/features/trips/components/NewTrip"));
const NewArchiveTrip = React.lazy(() => import("@/features/trips/components/NewArchiveTrip"));
const InspectionPage = React.lazy(
  () => import("@/features/operations/pages/InspectionPage/InspectionPage")
);
const InspectionListItemsPage = React.lazy(
  () => import("@/features/operations/pages/InspectionListItems/InspectionListItemsPage")
);
const ArchivedInspectionChecklistPage = React.lazy(
  () => import("@/features/operations/pages/ArchivedInspectionChecklistPage/ArchivedInspectionChecklistPage")
);
const ReimbursementsPage = React.lazy(
  () => import("@/features/operations/pages/ReimbursementsPage")
);
const ReimbursementDetailsPage = React.lazy(
  () => import("@/features/operations/pages/ReimbursementDetailsPage")
);
const SubscriptionsPage = React.lazy(() => import("@/features/operations/pages/SubscriptionsPage"));
const SubscriptionDetailsPage = React.lazy(
  () => import("@/features/operations/pages/SubscriptionDetailsPage")
);
const EditSubscriptionPage = React.lazy(
  () => import("@/features/operations/pages/EditSubscriptionPage")
);
const CopyReturnSubscriptionPage = React.lazy(
  () => import("@/features/admin/components/clients/subscriptions/CopyReturnSubscriptionPage")
);
const ArchiveSchedulesPage = React.lazy(
  () => import("@/features/operations/pages/archiveSchedules/ArchiveSchedulesPage")
);
const ArchiveSchedulesMarkCompletePage = React.lazy(
  () => import("@/features/operations/pages/archiveSchedules/ArchiveSchedulesMarkCompletePage")
);
const ArchiveRunDetailsPage = React.lazy(
  () => import("@/features/operations/pages/archiveSchedules/ArchiveRunDetailsPage")
);
const ArchiveTripsPage = React.lazy(() => import("@/features/operations/pages/ArchiveTripsPage"));
const ArchiveTripDetailsPage = React.lazy(
  () => import("@/features/operations/pages/ArchiveTripDetailsPage")
);
const EditArchiveTripPage = React.lazy(
  () => import("@/features/operations/pages/EditArchiveTripPage")
);
const ReturnArchiveTripPage = React.lazy(
  () => import("@/features/operations/pages/ReturnArchiveTripPage")
);
const OperationsDashboard = React.lazy(
  () => import("@/features/operations/pages/Dashboard/DashboardPage")
);
const SchedulesPage = React.lazy(
  () => import("@/features/operations/pages/SchedulesPage")
);
const MarkCompletePage = React.lazy(
  () => import("@/features/operations/pages/MarkCompletePage")
);
const ScheduleDetailsPage = React.lazy(
  () => import("@/features/operations/pages/ScheduleDetailsPage")
);
const TripSchedulingLogPage = React.lazy(
  () => import("@/features/operations/pages/TripSchedulingLogPage")
);
const ScheduleVersionsPage = React.lazy(
  () => import("@/features/operations/pages/ScheduleVersionsPage")
);
const RunStatusPage = React.lazy(() => import("@/features/operations/pages/RunStatusPage"));
const OnTimePerformancePage = React.lazy(
  () => import("@/features/operations/pages/OnTimePerformancePage")
);
const LateTripsPage = React.lazy(() => import("@/features/operations/pages/LateTripsPage"));
const AlertsPage = React.lazy(() => import("@/features/operations/pages/AlertsPage"));
const AlertDetailsPage = React.lazy(() => import("@/features/operations/pages/AlertDetailsPage"));
const MessagesPage = React.lazy(() => import("@/features/operations/pages/Messages/MessagesPage"));
const ExpensesPage = React.lazy(() => import("@/features/operations/pages/ExpensesPage"));
const ExpenseDetailsPage = React.lazy(
  () => import("@/features/operations/pages/ExpenseDetailsPage")
);
const PrintManifest = React.lazy(() => import("@/pages/PrintManifest"));

export const operationsRoutes = (AppLayout: React.FC<AppLayoutProps>): RouteObject[] => [
  {
    path: "operations/trips",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.TRIP_VIEW}>
          <Trips />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/trips/:tripId",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.TRIP_VIEW}>
          <Trips />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/scheduling",
    element: (
      <AppLayout>
        <ProtectedRoute
          apiPermission={PERMISSIONS.METAROUTE_VIEW}
        >
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-2">Scheduling</h1>
            <p className="text-text-muted">Coming Soon</p>
          </div>
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/archiveInspectionChecklists",
    element: (
      <AppLayout>
        <ProtectedRoute
          apiPermission={PERMISSIONS.ARCHIVE_INSPECTION_CHECKLIST_VIEW}
        >
          <InspectionPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/archiveInspectionChecklists/:id",
    element: (
      <AppLayout>
        <ProtectedRoute
          apiPermission={PERMISSIONS.ARCHIVE_INSPECTION_CHECKLIST_VIEW}
        >
          <ArchivedInspectionChecklistPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/archiveInspectionItems",
    element: (
      <AppLayout>
        <ProtectedRoute
          apiPermission={PERMISSIONS.ARCHIVE_INSPECTION_CHECKLIST_VIEW}
        >
          <InspectionListItemsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/archive-schedules",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.ARCHIVE_ROUTE_VIEW}>
          <ArchiveSchedulesPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/archive-schedules/:runId",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.ARCHIVE_ROUTE_VIEW}>
          <ArchiveRunDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/archive-schedules/:runId/history",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.ARCHIVE_ROUTE_UPDATE}>
          <ArchiveRunDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/archive-schedules/mark-complete",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.METAROUTE_MARK_COMPLETE_OPERATION} requiredFeature="operations">
          <ArchiveSchedulesMarkCompletePage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/archive-schedules/:runId",
    element: (
      <AppLayout>
        <ProtectedRoute
          apiPermission={PERMISSIONS.METAROUTE_MARK_COMPLETE_OPERATION}
        >
          <ArchiveSchedulesMarkCompletePage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/archive-schedules/:runId/history",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.ARCHIVE_ROUTE_UPDATE}>
          <ArchiveTripsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/archive-trips",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.ARCHIVE_TRIP_VIEW}>
          <ArchiveTripsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/archive-trips/reconcile",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.CLEAR_TRIPS_OPERATION}>
          <ArchiveTripsPage activeTab="reconcile" />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/archive-trips/review",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.TRIP_REVIEW_OPERATION}>
          <ArchiveTripsPage activeTab="review" />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/archive-trips/verify",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.ARCHIVE_POST_VERIFY_OPERATION}>
          <ArchiveTripsPage activeTab="verify" />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/archive-trips/:tripId",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.ARCHIVE_TRIP_VIEW}>
          <ArchiveTripDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/archive-trips/:tripId/edit",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.ARCHIVE_TRIP_UPDATE}>
          <EditArchiveTripPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/archive-trips/:tripId/return",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.ARCHIVE_TRIP_CREATE}>
          <ReturnArchiveTripPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/dashboard",
    element: (
      <AppLayout>
        <ProtectedRoute
          apiPermission={PERMISSIONS.DASHBOARD_VIEW}
          fallback={
            <React.Suspense fallback={null}>
              <DashboardNoAccessPage />
            </React.Suspense>
          }
        >
          <OperationsDashboard />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/runStatuses",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.RUN_VIEW}>
          <RunStatusPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/onTimePerformances",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.TRIP_VIEW}>
          <OnTimePerformancePage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/lateTrips",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.TRIP_VIEW}>
          <LateTripsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/schedules",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.METAROUTE_VIEW} requiredFeature="operations">
          <SchedulesPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/schedules/mark-complete",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.METAROUTE_MARK_COMPLETE_OPERATION} requiredFeature="operations">
          <MarkCompletePage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/schedules/:id",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.METAROUTE_VIEW} requiredFeature="operations">
          <ScheduleDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/trip-scheduling-log/:schedulingExplanationId",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.METAROUTE_VIEW} requiredFeature="operations">
          <TripSchedulingLogPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/schedules/:id/versions",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.METAROUTE_VIEW} requiredFeature="operations">
          <ScheduleVersionsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    // Schedule print manifest — standalone full-page print view (no nav/sidebar), opens in new tab
    path: "/operations/schedules/:id/print-manifest",
    element: (
      <ErrorBoundary fallback={ErrorState}>
        <AuthWrapper>
          <React.Suspense fallback={<LoadingState message="Loading manifest..." />}>
            <ProtectedRoute apiPermission={PERMISSIONS.METAROUTE_VIEW}>
              <PrintManifest />
            </ProtectedRoute>
          </React.Suspense>
        </AuthWrapper>
      </ErrorBoundary>
    ),
  },
  {
    path: "/operations/alerts",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.ALERT_VIEW}>
          <AlertsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/alerts/:alertId",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.ALERT_VIEW}>
          <AlertDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/messages",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.ALERT_VIEW}>
          <MessagesPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/reimbursements",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.REIMBURSEMENT_VIEW}>
          <ReimbursementsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/reimbursements/:reimbursementId",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.REIMBURSEMENT_VIEW}>
          <ReimbursementDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/subscriptions",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.SUBSCRIPTION_VIEW}>
          <SubscriptionsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/subscriptions/:subscriptionId",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.SUBSCRIPTION_VIEW}>
          <SubscriptionDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/subscriptions/:subscriptionId/edit",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.SUBSCRIPTION_UPDATE}>
          <EditSubscriptionPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/subscriptions/:subscriptionId/copy",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.SUBSCRIPTION_CREATE}>
          <CopyReturnSubscriptionPage actionType="copy" />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/subscriptions/:subscriptionId/return",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.SUBSCRIPTION_CREATE}>
          <CopyReturnSubscriptionPage actionType="return" />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/expenses",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.EXPENSE_VIEW}>
          <ExpensesPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/operations/expenses/:expenseId",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.EXPENSE_VIEW}>
          <ExpenseDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    // Legacy redirect: old /expenses/:expenseId links (e.g. from emails, bookmarks)
    // are forwarded to the canonical /operations/expenses/:expenseId route.
    path: "/expenses/:expenseId",
    element: <LegacyExpenseRedirect />,
  },
  {
    path: "/new-trip/:clientId",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.TRIP_CREATE}>
          <NewTrip />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/new-archive-trip/:clientId",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.ARCHIVE_TRIP_CREATE}>
          <NewArchiveTrip />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
];
